'use client'

import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/General/Button'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { TextLink } from '@/General/TextLink'
import { EmailSuggestionModal } from '../../account/update/controls/EmailUpdate/EmailSuggestionModal'
import { useCaptcha } from '@/AdvertisingModal/useCaptcha'
import { handleError } from '@/AdvertisingModal/utils/errorHandling'
import {
  useAddCustomerToSegmentMutation,
  useCreateCustomerMutation,
  useCheckEmailExistsQuery,
} from '@/generated/graphql'
import { EMAIL_REGEX } from '@/validation'
import { tracking } from '@/tracking'
import { LoginModal } from '@/MainLayout/Header/TopMenu/LoginModal'
import { DELUXE_EDITION_SEGMENT_ID } from './constants'
import { playfair } from './fonts'
import { splitFullName } from './utils'

type FormValues = {
  name: string
  email: string
}

type Props = {
  onSuccess: () => void
}

export function DeluxeSignupGuestForm({ onSuccess }: Props) {
  const hasAcceptedSuggestionRef = useRef(false)

  const [formErrors, setFormErrors] = useState<string[]>([])
  const [showAccountExistsLogin, setShowAccountExistsLogin] = useState(false)
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const [showEmailSuggestion, setShowEmailSuggestion] = useState(false)
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null)
  const [suggestionText, setSuggestionText] = useState('')
  const [originalEmailForModal, setOriginalEmailForModal] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const { verifyCaptcha } = useCaptcha({ enabled: true })
  const { mutateAsync: createCustomer } = useCreateCustomerMutation()
  const { mutateAsync: addToSegment } = useAddCustomerToSegmentMutation()

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<FormValues>({
    defaultValues: { name: '', email: '' },
  })

  const nameRegister = register('name', {
    required: 'Name is required',
    pattern: {
      value: /^[a-zA-Z]+\s+[a-zA-Z]+/,
      message: 'Please enter your first and last name',
    },
  })

  const emailRegister = register('email', {
    required: 'Email is required',
    pattern: {
      value: EMAIL_REGEX,
      message: 'Please enter a valid email address',
    },
  })

  const inputRingClass = (hasError: boolean) =>
    `block w-full rounded border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset transition-all duration-200 text-base ${
      hasError
        ? 'ring-red-500 focus:ring-red-500 focus:ring-2'
        : 'ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-500 hover:ring-gray-400'
    }`

  const onGuestSubmit = async (data: FormValues) => {
    setFormErrors([])
    setShowAccountExistsLogin(false)
    setSubmitting(true)

    try {
      try {
        await verifyCaptcha()
      } catch (e) {
        setFormErrors([
          handleError(
            e,
            undefined,
            'We could not verify the request. Please try again or email us at magic@vanishingincmagic.com.'
          ),
        ])
        return
      }

      const trimmedEmail = data.email.trim()
      const existsData = await useCheckEmailExistsQuery.fetcher({
        email: trimmedEmail,
      })()
      if (existsData.checkEmailExists) {
        setShowAccountExistsLogin(true)
        return
      }

      const { firstName, lastName } = splitFullName(data.name.trim())

      let created: Awaited<ReturnType<typeof createCustomer>>
      try {
        created = await createCustomer({
          firstName,
          lastName,
          email: trimmedEmail,
          howSignedUp: 1,
          addToNewsletter: true,
          skipEmailValidation: hasAcceptedSuggestionRef.current,
        })
      } catch (e) {
        setFormErrors([handleError(e)])
        return
      }

      if (!created.createCustomer.success) {
        const msg = created.createCustomer.message || ''
        const ev = created.createCustomer.emailValidation

        if (
          !hasAcceptedSuggestionRef.current &&
          ev?.suggestion &&
          ev?.correctedEmail
        ) {
          setSuggestedEmail(ev.correctedEmail)
          setSuggestionText(ev.suggestion || '')
          setOriginalEmailForModal(trimmedEmail)
          setShowEmailSuggestion(true)
          setFormErrors([
            ev.errorMessage ||
              msg ||
              'Please check your email address.',
          ])
          return
        }

        if (hasAcceptedSuggestionRef.current && ev?.suggestion) {
          let retry
          try {
            retry = await createCustomer({
              firstName,
              lastName,
              email: trimmedEmail,
              howSignedUp: 1,
              addToNewsletter: true,
              skipEmailValidation: true,
            })
          } catch (e) {
            setFormErrors([handleError(e)])
            return
          }

          if (!retry.createCustomer.success) {
            if (
              retry.createCustomer.message?.includes('already exists') ||
              retry.createCustomer.message?.includes('already registered') ||
              retry.createCustomer.message?.includes(
                'A customer with this email address already exists'
              )
            ) {
              setShowAccountExistsLogin(true)
              return
            }
            setFormErrors([
              retry.createCustomer.emailValidation?.errorMessage ||
                retry.createCustomer.message ||
                'Could not complete signup. Please check your details and try again.',
            ])
            return
          }
          created = retry
        } else {
          if (
            msg.toLowerCase().includes('already exists') ||
            msg.toLowerCase().includes('already registered') ||
            msg.includes('A customer with this email address already exists')
          ) {
            setShowAccountExistsLogin(true)
            return
          }
          setFormErrors([
            ev?.errorMessage ||
              msg ||
              'Could not complete signup. Please check your details and try again.',
          ])
          return
        }
      }

      const customerId = created.createCustomer.customerId ?? null
      if (customerId == null) {
        setFormErrors([
          'We could not finish registering you for notifications. Please contact magic@vanishingincmagic.com.',
        ])
        return
      }

      try {
        await addToSegment({
          segmentId: DELUXE_EDITION_SEGMENT_ID,
        })
      } catch (e) {
        setFormErrors([
          handleError(
            e,
            undefined,
            'Your account was created but we could not add you to the deluxe list. Please contact magic@vanishingincmagic.com.'
          ),
        ])
        return
      }

      hasAcceptedSuggestionRef.current = false
      tracking.identify(trimmedEmail, {
        customerId,
        firstName,
        lastName,
      })
      tracking.track('Deluxe edition list signup', { source: 'deluxe-books' })
      onSuccess()
    } finally {
      setSubmitting(false)
    }
  }

  const handleAcceptSuggestion = (acceptedEmail: string) => {
    hasAcceptedSuggestionRef.current = true
    setValue('email', acceptedEmail)
    setShowEmailSuggestion(false)
    setSuggestedEmail(null)
    setSuggestionText('')
    setOriginalEmailForModal('')
    requestAnimationFrame(() => {
      void handleSubmit(onGuestSubmit)()
    })
  }

  const handleDeclineSuggestion = () => {
    hasAcceptedSuggestionRef.current = true
    setShowEmailSuggestion(false)
    setSuggestedEmail(null)
    setSuggestionText('')
    setOriginalEmailForModal('')
    requestAnimationFrame(() => {
      void handleSubmit(onGuestSubmit)()
    })
  }

  return (
    <>
      <LoginModal
        isLoginOpen={isLoginOpen}
        setIsLoginOpen={setIsLoginOpen}
        redirectTo="/deluxe-books/"
        initialEmail={getValues('email')?.trim() || undefined}
        message="Sign in with the email you used before, then submit again to join the deluxe edition list."
      />
      <EmailSuggestionModal
        isOpen={showEmailSuggestion && !!suggestedEmail}
        onClose={handleDeclineSuggestion}
        suggestion={suggestionText}
        originalEmail={originalEmailForModal}
        correctedEmail={suggestedEmail || undefined}
        onAcceptSuggestion={handleAcceptSuggestion}
        onDecline={handleDeclineSuggestion}
      />

      <form
        onSubmit={handleSubmit(onGuestSubmit)}
        noValidate
        className="text-left max-w-lg mx-auto space-y-4"
      >
        <ErrorBox errors={formErrors} />

        {showAccountExistsLogin && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-sm">
            <p className="mb-2">
              An account with this email address already exists.
            </p>
            <button
              type="button"
              onClick={() => setIsLoginOpen(true)}
              className="text-blue-700 underline hover:text-blue-900 font-semibold cursor-pointer"
            >
              Log in to your existing account
            </button>
            <p className="mt-2 text-sm">
              Or use{' '}
              <TextLink href="/account/login/?redirect=/deluxe-books/">
                this link
              </TextLink>{' '}
              to log in and return here.
            </p>
          </div>
        )}

        <div>
          <label
            htmlFor="deluxe-name"
            className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
          >
            Name
          </label>
          <input
            id="deluxe-name"
            placeholder="First and last name"
            type="text"
            autoComplete="name"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'deluxe-name-error' : undefined}
            className={inputRingClass(!!errors.name)}
            {...nameRegister}
          />
          {errors.name?.message ? (
            <p
              id="deluxe-name-error"
              className="text-red-600 text-sm mt-1"
              role="alert"
            >
              {errors.name.message}
            </p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="deluxe-email"
            className="block text-sm font-semibold leading-6 text-gray-900 mb-2"
          >
            Email address
          </label>
          <input
            id="deluxe-email"
            placeholder="your@email.com"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'deluxe-email-error' : undefined}
            className={inputRingClass(!!errors.email)}
            {...emailRegister}
          />
          {errors.email?.message ? (
            <p
              id="deluxe-email-error"
              className="text-red-600 text-sm mt-1"
              role="alert"
            >
              {errors.email.message}
            </p>
          ) : null}
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className={`${playfair.className} w-full rounded-lg border-0 bg-gradient-to-br from-[#d4af37] to-[#b8941f] py-4 text-lg font-semibold uppercase tracking-wide text-white shadow-md transition hover:from-[#b8941f] hover:to-[#d4af37] hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:opacity-60 disabled:hover:translate-y-0`}
        >
          {submitting ? 'Please wait…' : 'Request Exclusive Access'}
        </Button>
      </form>
    </>
  )
}
